/** The right panel: the element palette, tools, edit and view switches, and the cave buttons. */

import { BLACK, DARK_GREY, GREY, LIGHT_BLUE, WHITE, YELLOW } from '../../render/palette';
import { KIT_ELEMENTS, elementName, paletteIndex } from '../model/palette';
import { TOOLS } from '../model/shapes';
import type { KitApi } from './api';
import { drawIcon } from './icons';
import type { Rect } from './layout';
import { INK, PALETTE, SIDE, SIDE_BTN_W, SIDE_COL, SIDE_THIRD } from './layout';
import { drawCellAt } from './look';
import { button, panel } from './widgets';

const ELEMENTS_Y = 14;
const TOOLS_Y = 104;
const EDIT_Y = 178;
const CAVE_Y = 234;

function drawPalette(k: KitApi): void {
  const { ui } = k;
  const s = ui.screen;
  panel(ui, { x: SIDE.x, y: ELEMENTS_Y, w: SIDE.w, h: 87 }, 'ELEMENTS', '[ ]');
  const atlas = k.atlas();
  const cycle = (d: number): void => {
    const i = paletteIndex(k.element);
    const next = KIT_ELEMENTS[(i + d + KIT_ELEMENTS.length) % KIT_ELEMENTS.length];
    if (next) k.selectElement(next.ch);
  };
  KIT_ELEMENTS.forEach((e, i) => {
    const x = PALETTE.x + (i % PALETTE.cols) * PALETTE.pitch;
    const y = PALETTE.y + Math.floor(i / PALETTE.cols) * PALETTE.pitch;
    const id = `el${i}`;
    s.fill(x, y, 16, 16, e.ch === ' ' ? DARK_GREY : BLACK);
    if (e.ch === ' ') s.fill(x + 1, y + 1, 14, 14, BLACK);
    drawCellAt(s, atlas, e.ch, x, y, ui.anim);
    if (e.ch === k.element) {
      s.box(x - 2, y - 2, 20, 20, YELLOW);
      s.box(x - 1, y - 1, 18, 18, BLACK);
    } else if (ui.hot(id)) s.box(x - 1, y - 1, 18, 18, WHITE);
    ui.add({ x: x - 1, y: y - 1, w: 18, h: 18, id, tip: e.tip, click: () => k.selectElement(e.ch), wheel: cycle });
  });
  // The selected element, large enough to read its name.
  const y = PALETTE.y + 3 * PALETTE.pitch + 1;
  s.fill(PALETTE.x, y, 16, 16, BLACK);
  drawCellAt(s, atlas, k.element, PALETTE.x, y, ui.anim);
  s.text(elementName(k.element), PALETTE.x + 22, y + 4, YELLOW);
}

function drawTools(k: KitApi): void {
  const { ui } = k;
  panel(ui, { x: SIDE.x, y: TOOLS_Y, w: SIDE.w, h: 71 }, 'TOOLS');
  TOOLS.forEach((t, i) => {
    const r = { x: SIDE_COL[i % 2] ?? 0, y: TOOLS_Y + 13 + Math.floor(i / 2) * 19, w: SIDE_BTN_W, h: 16 };
    const on = k.tool === t.id;
    button(ui, r, '', { id: `tool-${t.id}`, tip: t.tip, on, click: () => k.selectTool(t.id) });
    const hot = ui.hot(`tool-${t.id}`);
    drawIcon(ui.screen, t.id, r.x + 5, r.y + 4, on ? WHITE : hot ? YELLOW : LIGHT_BLUE);
    ui.screen.text(t.label, r.x + 17, r.y + 4, on ? WHITE : hot ? YELLOW : INK.text);
  });
}

function drawEdit(k: KitApi): void {
  const { ui, st } = k;
  panel(ui, { x: SIDE.x, y: EDIT_Y, w: SIDE.w, h: 53 }, 'EDIT');
  const row1 = EDIT_Y + 13;
  const row2 = EDIT_Y + 32;
  const undo = st.history.undoCount;
  const redo = st.history.redoCount;
  button(ui, { x: SIDE_COL[0], y: row1, w: SIDE_BTN_W, h: 16 }, '◄ UNDO', {
    id: 'undo', tip: `UNDO (CTRL+Z): ${undo} STEP${undo === 1 ? '' : 'S'} BACK`, disabled: undo === 0,
    click: () => k.run('undo'),
  });
  button(ui, { x: SIDE_COL[1], y: row1, w: SIDE_BTN_W, h: 16 }, 'REDO ►', {
    id: 'redo', tip: `REDO (CTRL+Y): ${redo} STEP${redo === 1 ? '' : 'S'} FORWARD`, disabled: redo === 0,
    click: () => k.run('redo'),
  });
  const third = (i: number): Rect => ({ x: SIDE_THIRD[i] ?? 0, y: row2, w: 46, h: 16 });
  const zoom = k.view.cell === 16 ? '1X' : '1/2X';
  button(ui, third(0), zoom, { id: 'zoom', tip: 'ZOOM: 1X OR 1/2X (Z)', click: () => k.run('zoom') });
  button(ui, third(1), 'GRID', { id: 'grid', tip: 'GRID LINES (G)', on: k.showGrid, click: () => k.run('grid') });
  button(ui, third(2), 'ROLL', {
    id: 'preview', tip: 'SHOW WHAT THE ? CELLS ROLL AT LEVEL 1 (V)', on: k.preview, click: () => k.run('preview'),
  });
}

function drawCave(k: KitApi): void {
  const { ui, st } = k;
  const info = st.info;
  panel(ui, { x: SIDE.x, y: CAVE_Y, w: SIDE.w, h: SIDE.y + SIDE.h - CAVE_Y }, 'CAVE');
  const w2 = SIDE_BTN_W;
  const at = (col: number, row: number, w = w2): Rect => ({
    x: SIDE_COL[col] ?? 0, y: CAVE_Y + 36 + row * 19, w, h: 16,
  });
  button(ui, { x: SIDE_COL[0], y: CAVE_Y + 13, w: 142, h: 20 }, '► TEST', {
    id: 'test', tip: info.errors > 0 ? 'TEST: FIX THE ERRORS FIRST (T)' : 'TEST: PLAY IT NOW; ESC COMES BACK HERE (T)',
    ink: info.errors > 0 ? GREY : INK.ok, click: () => k.run('test'),
  });
  button(ui, at(0, 0), 'SAVE', { id: 'save', tip: 'SAVE TO YOUR LIBRARY (CTRL+S)', click: () => k.run('save') });
  button(ui, at(1, 0), 'SHARE', { id: 'share', tip: 'SHARE: A CODE AND A LINK (C)', click: () => k.run('share') });
  button(ui, at(0, 1), 'NEW', { id: 'new', tip: 'START A NEW CAVE (N)', click: () => k.run('new') });
  button(ui, at(1, 1), 'LIBRARY', { id: 'library', tip: 'YOUR SAVED CAVES (O)', click: () => k.run('library') });
  button(ui, at(0, 2), 'HELP', { id: 'help', tip: 'KEYS AND MOUSE (F1)', click: () => k.run('help') });
  button(ui, at(1, 2), 'BACK', { id: 'back', tip: 'BACK TO THE TITLE (ESC)', click: () => k.run('back') });
  const s = ui.screen;
  const y = CAVE_Y + 36 + 3 * 19 + 2;
  s.text('◆', SIDE_COL[0], y, YELLOW);
  s.text(`${info.diamonds} ON THE MAP`, SIDE_COL[0] + 12, y, INK.text);
  s.text(`${info.potential} TO BE HAD`, SIDE_COL[0] + 12, y + 10, INK.text);
  ui.add({
    x: SIDE_COL[0], y: y - 1, w: 142, h: 20, id: 'stats',
    tip: 'DIAMONDS AT LEVEL 1, AND ALL THAT CAN BE HAD WITH BUTTERFLIES, AMOEBA AND MAGIC WALLS',
  });
}

export function drawSide(k: KitApi): void {
  drawPalette(k);
  drawTools(k);
  drawEdit(k);
  drawCave(k);
}
